// ===================================================================
// KAIXO - Visor de imagen a pantalla completa con pinch-zoom + pan
// ===================================================================
// Gestos soportados:
//   - Pinch con 2 dedos (móvil): zoom hacia el centro de los dedos
//   - Arrastrar con 1 dedo cuando está zoomed: pan
//   - Doble-tap / doble-click: alterna zoom 1x ↔ 2.5x en el punto tocado
//   - Rueda del ratón: zoom hacia el cursor (desktop)
//   - Swipe hacia abajo estando a 1x: cierra el viewer (gesto iOS-style)
//   - Botón ✕ o tap en fondo (a 1x): cierra
//   - ESC: cierra
//
// Límites: zoom 1x a 5x. Pan acotado al borde de la imagen (no se
// puede arrastrar más allá del área visible cuando la imagen ya cabe).
// Uso: <ImageViewer src={url} onClose={() => setSrc(null)} />
// ===================================================================

import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { downloadMedia } from '../../utils/downloadMedia';
import './ImageViewer.css';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_SCALE = 2.5;
const CLOSE_SWIPE_THRESHOLD = 120; // px verticales para cerrar cuando está a 1x

function ImageViewer({ src, onClose }) {
    const imgRef = useRef(null);
    const wrapRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Estado del transform. Usamos useRef para evitar re-renders en cada
    // frame de gesto; el DOM se actualiza directamente via style.transform.
    const transformRef = useRef({ scale: 1, x: 0, y: 0 });
    // Estado del gesto activo
    const gestureRef = useRef({
        // pan
        panning: false,
        startX: 0, startY: 0, startTx: 0, startTy: 0,
        // pinch
        pinching: false,
        startDist: 0, startScale: 1,
        pinchCx: 0, pinchCy: 0,
        startTxPinch: 0, startTyPinch: 0,
        // doble-tap
        lastTapTime: 0,
        lastTapX: 0, lastTapY: 0,
        // swipe-to-close (solo cuando scale === 1)
        swipeStartY: 0, swipedY: 0
    });

    // Aplica el transform al DOM. Imperativo para fluidez.
    const applyTransform = useCallback(() => {
        const el = imgRef.current;
        if (!el) return;
        const { scale, x, y } = transformRef.current;
        el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }, []);

    // Clampea el pan para que la imagen no se salga del área visible.
    const clampPan = useCallback(() => {
        const el = imgRef.current;
        const wrap = wrapRef.current;
        if (!el || !wrap) return;
        const { scale } = transformRef.current;
        // Bounding sin transform aplicado
        const baseW = el.offsetWidth;
        const baseH = el.offsetHeight;
        const scaledW = baseW * scale;
        const scaledH = baseH * scale;
        const containerW = wrap.clientWidth;
        const containerH = wrap.clientHeight;
        // Margen máximo de desplazamiento: la mitad del exceso de la imagen sobre el contenedor
        const maxX = Math.max(0, (scaledW - containerW) / 2);
        const maxY = Math.max(0, (scaledH - containerH) / 2);
        transformRef.current.x = Math.min(maxX, Math.max(-maxX, transformRef.current.x));
        transformRef.current.y = Math.min(maxY, Math.max(-maxY, transformRef.current.y));
    }, []);

    const resetTransform = useCallback((animated = true) => {
        const el = imgRef.current;
        if (!el) return;
        if (animated) el.style.transition = 'transform 0.2s ease-out';
        transformRef.current = { scale: 1, x: 0, y: 0 };
        applyTransform();
        if (animated) setTimeout(() => { if (el) el.style.transition = ''; }, 220);
    }, [applyTransform]);

    // Zoom hacia un punto (x, y) en coordenadas del contenedor.
    const zoomAt = useCallback((newScale, clientX, clientY, animated = false) => {
        const el = imgRef.current;
        const wrap = wrapRef.current;
        if (!el || !wrap) return;
        const wrapRect = wrap.getBoundingClientRect();
        const cx = clientX - wrapRect.left - wrapRect.width / 2; // punto relativo al centro del contenedor
        const cy = clientY - wrapRect.top - wrapRect.height / 2;
        const { scale, x, y } = transformRef.current;
        const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
        // Para que el punto bajo el dedo/cursor quede fijo visualmente,
        // desplazamos el translate proporcionalmente al cambio de escala.
        const ratio = clampedScale / scale;
        transformRef.current.scale = clampedScale;
        transformRef.current.x = cx - (cx - x) * ratio;
        transformRef.current.y = cy - (cy - y) * ratio;
        // Si volvemos a 1x, resetear pan al centro
        if (clampedScale <= MIN_SCALE + 0.01) {
            transformRef.current.x = 0;
            transformRef.current.y = 0;
            transformRef.current.scale = MIN_SCALE;
        }
        clampPan();
        if (animated) el.style.transition = 'transform 0.2s ease-out';
        applyTransform();
        if (animated) setTimeout(() => { if (el) el.style.transition = ''; }, 220);
    }, [clampPan, applyTransform]);

    // ── Touch events ─────────────────────────────────────────────
    const onTouchStart = useCallback((e) => {
        const el = imgRef.current;
        if (!el) return;
        el.style.transition = '';
        const g = gestureRef.current;
        if (e.touches.length === 2) {
            // Inicio pinch
            const [t1, t2] = e.touches;
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            g.pinching = true;
            g.panning = false;
            g.startDist = Math.hypot(dx, dy);
            g.startScale = transformRef.current.scale;
            g.pinchCx = (t1.clientX + t2.clientX) / 2;
            g.pinchCy = (t1.clientY + t2.clientY) / 2;
            g.startTxPinch = transformRef.current.x;
            g.startTyPinch = transformRef.current.y;
            e.preventDefault();
        } else if (e.touches.length === 1) {
            const t = e.touches[0];
            const now = Date.now();
            // Detectar doble-tap (solo si no estaba paneando hace un momento)
            const dt = now - g.lastTapTime;
            const dx = Math.abs(t.clientX - g.lastTapX);
            const dy = Math.abs(t.clientY - g.lastTapY);
            if (dt < DOUBLE_TAP_MS && dx < 30 && dy < 30) {
                g.lastTapTime = 0;
                const current = transformRef.current.scale;
                const nextScale = current > MIN_SCALE + 0.1 ? MIN_SCALE : DOUBLE_TAP_SCALE;
                zoomAt(nextScale, t.clientX, t.clientY, true);
                e.preventDefault();
                return;
            }
            g.lastTapTime = now;
            g.lastTapX = t.clientX;
            g.lastTapY = t.clientY;
            // Iniciar pan (o swipe-to-close si está a 1x)
            if (transformRef.current.scale > MIN_SCALE + 0.01) {
                g.panning = true;
                g.startX = t.clientX;
                g.startY = t.clientY;
                g.startTx = transformRef.current.x;
                g.startTy = transformRef.current.y;
            } else {
                g.swipeStartY = t.clientY;
                g.swipedY = 0;
            }
        }
    }, [zoomAt]);

    const onTouchMove = useCallback((e) => {
        const g = gestureRef.current;
        if (g.pinching && e.touches.length === 2) {
            const [t1, t2] = e.touches;
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const dist = Math.hypot(dx, dy);
            const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * (dist / g.startDist)));
            // Zoom manteniendo el punto medio entre dedos fijo
            const wrap = wrapRef.current;
            if (!wrap) return;
            const wrapRect = wrap.getBoundingClientRect();
            const cx = g.pinchCx - wrapRect.left - wrapRect.width / 2;
            const cy = g.pinchCy - wrapRect.top - wrapRect.height / 2;
            const ratio = newScale / g.startScale;
            transformRef.current.scale = newScale;
            transformRef.current.x = cx - (cx - g.startTxPinch) * ratio;
            transformRef.current.y = cy - (cy - g.startTyPinch) * ratio;
            clampPan();
            applyTransform();
            e.preventDefault();
        } else if (g.panning && e.touches.length === 1) {
            const t = e.touches[0];
            transformRef.current.x = g.startTx + (t.clientX - g.startX);
            transformRef.current.y = g.startTy + (t.clientY - g.startY);
            clampPan();
            applyTransform();
            e.preventDefault();
        } else if (e.touches.length === 1 && transformRef.current.scale <= MIN_SCALE + 0.01) {
            // Swipe vertical para cerrar (solo a 1x)
            const t = e.touches[0];
            const dy = t.clientY - g.swipeStartY;
            g.swipedY = dy;
            const wrap = wrapRef.current;
            if (wrap) {
                const opacity = Math.max(0.4, 1 - Math.abs(dy) / 400);
                wrap.style.background = `rgba(0,0,0,${0.92 * opacity})`;
                const el = imgRef.current;
                if (el) el.style.transform = `translate3d(0, ${dy}px, 0)`;
            }
        }
    }, [clampPan, applyTransform]);

    const onTouchEnd = useCallback((e) => {
        const g = gestureRef.current;
        if (g.pinching && e.touches.length < 2) {
            g.pinching = false;
            // Si el pinch nos llevó por debajo de 1x, snap a 1x
            if (transformRef.current.scale < MIN_SCALE + 0.05) {
                resetTransform(true);
            }
        }
        if (g.panning && e.touches.length === 0) {
            g.panning = false;
        }
        // Cerrar si el swipe-to-close supera el umbral
        const wrap = wrapRef.current;
        if (g.swipedY && transformRef.current.scale <= MIN_SCALE + 0.01) {
            if (Math.abs(g.swipedY) > CLOSE_SWIPE_THRESHOLD) {
                onClose?.();
            } else {
                // rebote
                if (wrap) wrap.style.background = '';
                resetTransform(true);
            }
            g.swipedY = 0;
        }
    }, [onClose, resetTransform]);

    // ── Mouse/desktop: rueda zoom + doble-click ──────────────────
    const onWheel = useCallback((e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.85 : 1.18;
        zoomAt(transformRef.current.scale * delta, e.clientX, e.clientY, false);
    }, [zoomAt]);

    const onDoubleClick = useCallback((e) => {
        const current = transformRef.current.scale;
        const nextScale = current > MIN_SCALE + 0.1 ? MIN_SCALE : DOUBLE_TAP_SCALE;
        zoomAt(nextScale, e.clientX, e.clientY, true);
    }, [zoomAt]);

    // Drag con ratón cuando está zoomed
    useEffect(() => {
        const el = imgRef.current;
        if (!el) return;
        let dragging = false;
        let startX = 0, startY = 0, startTx = 0, startTy = 0;
        const down = (e) => {
            if (transformRef.current.scale <= MIN_SCALE + 0.01) return;
            dragging = true;
            startX = e.clientX; startY = e.clientY;
            startTx = transformRef.current.x; startTy = transformRef.current.y;
            e.preventDefault();
        };
        const move = (e) => {
            if (!dragging) return;
            transformRef.current.x = startTx + (e.clientX - startX);
            transformRef.current.y = startTy + (e.clientY - startY);
            clampPan();
            applyTransform();
        };
        const up = () => { dragging = false; };
        el.addEventListener('mousedown', down);
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return () => {
            el.removeEventListener('mousedown', down);
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
    }, [clampPan, applyTransform]);

    // ESC para cerrar
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Cuando la imagen carga, reset y quedamos listos
    useEffect(() => {
        setReady(false);
        resetTransform(false);
    }, [src, resetTransform]);

    if (!src) return null;

    // Tap en el fondo (solo a 1x) cierra. Tap en la imagen no cierra.
    const onBackgroundClick = (e) => {
        if (e.target !== e.currentTarget) return;
        if (transformRef.current.scale <= MIN_SCALE + 0.01) onClose?.();
    };

    const handleDownload = async (e) => {
        e?.stopPropagation();
        if (downloading) return;
        setDownloading(true);
        const r = await downloadMedia(src, 'image');
        setDownloading(false);
        try {
            const msg = r.ok ? '✅ Imagen guardada' : `❌ ${r.error || 'Error al descargar'}`;
            window.dispatchEvent(new CustomEvent('show_notification', { detail: msg }));
        } catch {}
    };

    return ReactDOM.createPortal(
        <div
            className="imgv-overlay"
            ref={wrapRef}
            onClick={onBackgroundClick}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onWheel={onWheel}
        >
            <button className="imgv-close" onClick={onClose}>✕</button>
            <button className="imgv-download" onClick={handleDownload} aria-label="Descargar" disabled={downloading}>
                {downloading ? '…' : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 4v12" />
                        <polyline points="6 11 12 17 18 11" />
                        <path d="M5 20h14" />
                    </svg>
                )}
            </button>
            <img
                ref={imgRef}
                src={src}
                alt=""
                className={`imgv-img ${ready ? 'is-ready' : ''}`}
                onLoad={() => setReady(true)}
                onDoubleClick={onDoubleClick}
                draggable={false}
            />
        </div>,
        document.body
    );
}

export default ImageViewer;
